import React, { useMemo } from "react";
import {
    addMinutes,
    format,
    isBefore,
    isAfter,
    isSameDay,
    parseISO,
    setHours,
    setMinutes,
} from "date-fns";
import { AppointmentEvent, MyCalendarEvent } from "../hooks/useCalendarState";

export interface EventTypeD {
    start: string; // ISO string
    end: string;   // ISO string
    resourceId: number;
    type: "appointment" | "offtime";
}

interface AvailableSlotSelectProps {
    events: MyCalendarEvent[];
    resourceId: number;
    date: Date | any;
    workStart?: string; // e.g. "08:00"
    workEnd?: string;   // e.g. "20:00"
    slotInterval?: number; // default 10 min
    selectedSlot?: string;
    onSelect: (value: string) => void;
    duration?: number;
    ignoreEventId?: number;
    disabled?: boolean;
}

const parseToDate = (value: string | Date): Date | null => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value === "string") {
        const parsed = parseISO(value);
        if (!isNaN(parsed.getTime())) return parsed;
    }
    return null;
};
const AvailableSlotSelect: React.FC<AvailableSlotSelectProps> = ({
    events,
    resourceId,
    date,
    workStart,
    workEnd,
    slotInterval = 10,
    selectedSlot,
    onSelect,
    duration,
    ignoreEventId,
    disabled: selectDisabled = false,
}) => {

    const slots = useMemo(() => {
        const parsedDate = parseToDate(date) || new Date();
        const resourceOffTimes = events.filter((ev) => {
            const evStart = parseToDate(ev.start);
            return (
                ev.type === "offtime" &&
                Number(ev.resourceId) === Number(resourceId) &&
                evStart &&
                isSameDay(evStart, parsedDate)
            );
        });

        let startH = 8, startM = 0;
        let endH = 20, endM = 0;

        // Find off-time starting at midnight
        const morningOff = resourceOffTimes.find((ev) => {
            const evStart = parseToDate(ev.start);
            return evStart && evStart.getHours() === 0 && evStart.getMinutes() === 0;
        });
        if (morningOff) {
            const evEnd = parseToDate(morningOff.end);
            if (evEnd) {
                startH = evEnd.getHours();
                startM = evEnd.getMinutes();
            }
        }

        // Find off-time ending at midnight
        const eveningOff = resourceOffTimes.find((ev) => {
            const evEnd = parseToDate(ev.end);
            return evEnd && evEnd.getHours() === 23 && evEnd.getMinutes() >= 50;
        });
        if (eveningOff) {
            const evStart = parseToDate(eveningOff.start);
            if (evStart) {
                endH = evStart.getHours();
                endM = evStart.getMinutes();
            }
        }

        const finalWorkStart = workStart || `${String(startH).padStart(2, '0')}:${String(startM).padStart(2, '0')}`;
        const finalWorkEnd = workEnd || `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;

        const [sH, sM] = finalWorkStart.split(":").map(Number);
        const [eH, eM] = finalWorkEnd.split(":").map(Number);

        const dayStart = setMinutes(setHours(parsedDate, sH), sM);
        const dayEnd = setMinutes(setHours(parsedDate, eH), eM);

        const resourceEvents = events.filter((ev) => {
            const evStart = parseToDate(ev.start);
            return (
                Number(ev.resourceId) === Number(resourceId) &&
                evStart &&
                isSameDay(evStart, parsedDate)
            );
        });

        const generatedSlots: { time: string; display: string; disabled: boolean }[] = [];

        let current = new Date(dayStart);

        while (isBefore(current, dayEnd)) {
            const next = addMinutes(current, slotInterval);
            const slotEnd = duration && duration > 0 ? addMinutes(current, duration) : next;

            const isOutsideWorkingHours = isAfter(slotEnd, dayEnd);

            const isBusy = isOutsideWorkingHours || resourceEvents.some((ev) => {
                const evStart = parseToDate(ev.start);
                const evEnd = parseToDate(ev.end);
                if (!evStart || !evEnd) return false;
                if (ignoreEventId && ev.type === "appointment" && Number(ev.id) === Number(ignoreEventId)) {
                    return false;
                }
                return isBefore(current, evEnd) && isAfter(slotEnd, evStart);
            });

            generatedSlots.push({
                time: format(current, "HH:mm"), // internal value (24-hour)
                display: format(current, "hh:mm a"), // visible text (AM/PM)
                disabled: isBusy,
            });

            current = next;
        }

        // The appointment's own start time may be off the slot grid (e.g. a
        // 1:15 PM booking from the booking site) or outside these hours. Keep it
        // as an option, or the select falls back to the first slot and shows a
        // start time the appointment doesn't have.
        if (selectedSlot && !generatedSlots.some((s) => s.time === selectedSlot)) {
            const [h, m] = selectedSlot.split(":").map(Number);
            generatedSlots.push({
                time: selectedSlot,
                display: format(setMinutes(setHours(parsedDate, h), m), "hh:mm a"),
                disabled: false,
            });
            generatedSlots.sort((a, b) => a.time.localeCompare(b.time));
        }

        return generatedSlots;
    }, [events, resourceId, date, workStart, workEnd, slotInterval, duration, ignoreEventId, selectedSlot]);

    return (
        <div className="flex flex-col gap-2 w-full">
            <label
                htmlFor="availableSlot"
                className="text-sm font-medium text-gray-700"
            >
                Start Time *
            </label>
            <select
                name="startTime"
                id="availableSlot"
                value={selectedSlot ?? ""}
                disabled={selectDisabled}
                onChange={(e) => onSelect(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
                <option value="" disabled>
                    -- Select a time slot --
                </option>

                {slots.map(({ time, display, disabled }) => (
                    <option
                        key={time}
                        value={time}
                        disabled={disabled}
                        className={
                            disabled
                                ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                                : "text-gray-900"
                        }
                    >
                        {display} {disabled ? "(Unavailable)" : ""}
                    </option>
                ))}
            </select>

            {!selectDisabled && slots.every((s) => s.disabled) && (
                <p className="text-xs text-red-500 mt-1">
                    No available slots for this day.
                </p>
            )}
        </div>
    );
};

export default AvailableSlotSelect;
